import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import {
  applyPlainTextEditToCptHtml,
  cptHtmlToPlainText,
  cptTemplateHtmlSx,
  formatCptBulletsForDisplay,
  isCptStyledHtml,
  sanitizeCptTemplateHtml,
} from '../../utils/cptTemplateHtml';

/**
 * Section 9 & 11 template fields: CPT colour preview OR plain-text editor — one visible at a time.
 */
const CptTemplateTextField = ({
  field,
  disabled,
  placeholder,
  showCptPreview = true,
  sx = {},
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const lastStoredRef = useRef(field.value ?? '');
  const sourceHtmlRef = useRef('');

  const stored = field.value ?? '';
  const hasCptHtml = isCptStyledHtml(stored);
  // safeHtml stays the untouched sanitized value — beginEdit() below keeps a
  // reference to it for applyPlainTextEditToCptHtml's diffing, which must
  // see the model's actual output, not a display-only reformatting.
  const safeHtml = hasCptHtml ? sanitizeCptTemplateHtml(stored) : '';
  // formatCptBulletsForDisplay is purely presentational (hanging-indent for
  // the •/o bullet markers) — only used for what's actually rendered below.
  const displayHtml = formatCptBulletsForDisplay(safeHtml);
  const showColoredView = showCptPreview && Boolean(safeHtml) && !editMode;

  // New template generation replaced the field — show colour preview again
  useEffect(() => {
    if (stored !== lastStoredRef.current && isCptStyledHtml(stored)) {
      setEditMode(false);
      setEditDraft('');
    }
    lastStoredRef.current = stored;
  }, [stored]);

  const beginEdit = () => {
    sourceHtmlRef.current = hasCptHtml ? safeHtml || String(stored) : '';
    setEditDraft(hasCptHtml ? cptHtmlToPlainText(stored) : String(stored));
    setEditMode(true);
  };

  const backToColourPreview = () => {
    setEditMode(false);
    setEditDraft('');
    sourceHtmlRef.current = '';
  };

  const savePlainEdit = () => {
    const value =
      sourceHtmlRef.current && isCptStyledHtml(sourceHtmlRef.current)
        ? applyPlainTextEditToCptHtml(sourceHtmlRef.current, editDraft)
        : editDraft;
    field.onChange({ target: { name: field.name, value } });
    field.onBlur({ target: { name: field.name } });
    setEditMode(false);
    setEditDraft('');
    sourceHtmlRef.current = '';
  };

  return (
    <Box sx={{ width: '100%', p: showColoredView ? 1.5 : 0 }}>
      {showColoredView && (
        <>
          <Box
            sx={{
              p: 1.5,
              bgcolor: '#fafafa',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              ...cptTemplateHtmlSx,
            }}
            dangerouslySetInnerHTML={{ __html: displayHtml }}
          />
          {!disabled && (
            <Button
              size="small"
              variant="text"
              onClick={beginEdit}
              sx={{ mt: 0.5, textTransform: 'none', fontSize: '12px' }}
            >
              Edit as plain text
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '11px' }}>
            Edit as plain text to tweak wording — colours are preserved when you save. Re-check{' '}
            <strong>Template</strong> to fully regenerate from intake.
          </Typography>
        </>
      )}

      {editMode && (
        <>
          <TextField
            name={field.name}
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            multiline
            variant="outlined"
            fullWidth
            disabled={disabled}
            placeholder={placeholder}
            autoFocus
            helperText="Your edits keep CPT colours (blue/green/black). Click Save plain text, then Save draft / Submit on the form to persist."
            FormHelperTextProps={{ sx: { mx: 0, mt: 0.5, fontSize: '11px' } }}
            sx={{
              width: '100%',
              '& fieldset': { border: '1px solid #1976d2' },
              ...sx,
            }}
          />
          {!disabled && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                size="small"
                variant="contained"
                onClick={savePlainEdit}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                Save plain text
              </Button>
              {hasCptHtml && (
                <Button
                  size="small"
                  variant="text"
                  onClick={backToColourPreview}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      {!showColoredView && !editMode && (
        <TextField
          name={field.name}
          value={String(stored)}
          onChange={field.onChange}
          onBlur={field.onBlur}
          multiline
          variant="outlined"
          fullWidth
          disabled={disabled}
          placeholder={placeholder}
          helperText="Check Template to generate CPT-coloured protocol language."
          FormHelperTextProps={{ sx: { mx: 0, mt: 0.5, fontSize: '11px' } }}
          sx={{
            width: '100%',
            '& fieldset': { border: 'none' },
            ...sx,
          }}
        />
      )}
    </Box>
  );
};

export const CptColorLegend = () => (
  <Box
    sx={{
      mb: 2,
      p: 1.5,
      bgcolor: '#f8f9fa',
      border: '1px solid #dbe3eb',
      borderRadius: 1,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
      alignItems: 'center',
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>
      CPT colour key (Section 9 & 11 template preview):
    </Typography>
    <Typography variant="caption" sx={{ color: '#212121' }}>
      ■ Common / suggested
    </Typography>
    <Typography variant="caption" sx={{ color: '#1565C0' }}>
      ■ Variable
    </Typography>
    <Typography variant="caption" sx={{ color: '#2E7D32', fontStyle: 'italic' }}>
      ■ Example (intake-supported only)
    </Typography>
    <Typography variant="caption" sx={{ color: '#888' }}>
      (Instructional red text is never shown in output)
    </Typography>
  </Box>
);

export default CptTemplateTextField;
